import { Spacer } from "@/components/Spacer";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import { motion, useInView } from "framer-motion";
import { useRef } from "react";

export default function Section03({ isMobile }: { isMobile: boolean }) {
  const getNewLineWhenMobile = () => {
    return isMobile ? "\n" : "";
  };

  const sectionRef = useRef(null);
  const isSectionInView = useInView(sectionRef, {
    once: true,
    amount: 0.5,
  });

  return (
    <div className="flex flex-col items-center desktop:py-[100px] desktop:px-[32px] py-[80px] px-[24px] bg-[#ffffff]">
      <motion.div
        id="section-03-01"
        className="flex flex-col items-center"
        ref={sectionRef}
        initial={{ opacity: 0, y: 30 }}
        animate={isSectionInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
        transition={{
          type: "spring",
          stiffness: 200,
          damping: 80,
          mass: 1,
          delay: 0.2,
        }}
      >
        <div
          className="font-eng-01-semibold text-[#5364F2] desktop:text-[18px] text-[13px] leading-[100%]"
          style={{ letterSpacing: "-0.02em" }}
        >
          Do you have any symptoms?
        </div>

        <Spacer height={12} />
        <div
          className="font-eng-01-bold text-[#17191C] desktop:text-[32px] text-[24px] leading-[130%] text-center"
          style={{ letterSpacing: "-0.03em" }}
        >
          Check your potential{" "}
          <span className="desktop:hidden block">{"\n"}</span> condition the way
          you prefer.
        </div>
        <Spacer height={isMobile ? 16 : 12} />
        <div
          className="font-eng-01-medium text-[#17191C] desktop:text-[24px] text-[15px] leading-[100%]"
          style={{ letterSpacing: "-0.02em" }}
        >
          Make quick selections or chat freely with our AI.
        </div>
      </motion.div>
      <Spacer height={isMobile ? 40 : 60} />
      <AiChat isMobile={isMobile} />
      <Spacer height={24} />
      <Survey isMobile={isMobile} />
    </div>
  );
}

const AiChat = ({ isMobile }: { isMobile: boolean }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: "0px 0px -20% 0px",
  });

  return (
    <motion.div
      ref={ref}
      className="desktop:max-w-[960px] max-w-[342px] w-full bg-[#EFEEFC] desktop:pl-[80px] desktop:h-[400px] h-[413px] desktop:pt-[0px] pt-[32px] flex desktop:flex-row flex-col rounded-[20px]"
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
      transition={{
        type: "spring",
        stiffness: 300,
        damping: 100,
        mass: 1,
        delay: 0.2,
      }}
    >
      <div className="w-full h-full flex flex-col justify-center items-start desktop:pl-[0px] pl-[32px]">
        <Tip
          title="AI Chatbot"
          backgroundColor="rgba(83, 100, 242, 0.15)"
          textColor="#5364F2"
        />
        <Spacer height={isMobile ? 12 : 16} />
        <div
          className="font-eng-bold desktop:text-[22px] text-[18px] leading-[120%] text-[#17191C]"
          style={{ letterSpacing: "-0.03em" }}
        >
          From unusual symptoms
          <span className="desktop:block hidden">{"\n"}</span> to
          <span className="desktop:hidden block">{"\n"}</span> general health
          questions, ask Healthier’s AI anything
        </div>
        <Spacer height={isMobile ? 12 : 24} />
        <div
          className="font-eng-regular desktop:text-[15px] text-[14px] desktop:leading-[140%] leading-[130%] text-[#2E323A]"
          style={{ letterSpacing: "-0.02em" }}
        >
          Get professional health advice anytime,
          <span className="desktop:hidden block">{"\n"}</span> anywhere with
          Healthier's Doctor AI
          <span className="desktop:hidden block">{"\n"}</span> powered by the
          CIERA model.
        </div>
      </div>

      <div className="w-full h-full flex desktop:items-end items-center justify-center pt-[34px]">
        <div
          id="lottie-container"
          className="desktop:h-max desktop:w-[565px] px-[0px] w-full h-full flex desktop:items-end items-center justify-center"
        >
          <DotLottieReact
            src={"/assets/lottie/section-03-01-eng.lottie"}
            autoplay
            loop
          />
        </div>
      </div>
    </motion.div>
  );
};

const Survey = ({ isMobile }: { isMobile: boolean }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: "0px 0px -20% 0px",
  });

  return (
    <motion.div
      ref={ref}
      className="desktop:max-w-[960px] max-w-[342px] w-full bg-[#EBF7FC] desktop:pr-[0px] desktop:h-[400px] h-max desktop:pt-[0px] pt-[32px] flex desktop:flex-row flex-col-reverse rounded-[20px] desktop:items-center"
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
      transition={{
        type: "spring",
        stiffness: 300,
        damping: 100,
        mass: 1,
        delay: 0.2,
      }}
    >
      <div className="desktop:w-max w-[342px] h-full flex items-end justify-center desktop:pt-[0px] pt-[34px]">
        <div
          id="lottie-container"
          className="desktop:w-[565px] desktop:h-[290px] w-full h-full flex items-end justify-center"
        >
          <DotLottieReact
            src={"/assets/lottie/section-03-02-eng.lottie"}
            autoplay
            loop
          />
        </div>
      </div>

      <div className="h-max flex flex-col justify-end desktop:justify-center items-start desktop:pl-[32px] pl-[32px]">
        <Tip
          title="Guided Symptom Questionnaire"
          backgroundColor="rgba(0,180,255,0.15)"
          textColor="#00B4FF"
        />
        <Spacer height={isMobile ? 12 : 16} />
        <div
          className="font-eng-bold desktop:text-[22px] text-[18px] leading-[120%] text-[#17191C]"
          style={{ letterSpacing: " -0.03em" }}
        >
          Check your symptoms quickly <br />
          <span className="desktop:block hidden">{"\n"}</span> with simple
          selections
        </div>
        <Spacer height={isMobile ? 12 : 24} />
        <div
          className="font-eng-regular desktop:text-[15px] text-[14px] desktop:leading-[140%] leading-[130%] text-[#2E323A]"
          style={{ letterSpacing: "-0.02em" }}
        >
          Get a fast prediction of possible conditions
          <span className="desktop:block hidden">{"\n"}</span> through tailored
          questions
        </div>
      </div>
    </motion.div>
  );
};

const Tip = ({
  title,
  backgroundColor,
  textColor,
}: {
  title: string;
  backgroundColor: string;
  textColor: string;
}) => {
  return (
    <div
      className="px-[12px] desktop:py-[12px] py-[8px] rounded-[8px] flex justify-center items-center w-max"
      style={{ backgroundColor: backgroundColor }}
    >
      <span
        className="font-eng-bold desktop:text-[14px] text-[15px] leading-[1.2em]"
        style={{ letterSpacing: "-0.04em", color: textColor }}
      >
        {title}
      </span>
    </div>
  );
};
